// Weidner override of the blue-haven HeroImageBG.
//
// Diff from the parent:
//   • Adds an OPTIONAL subtitle rendered directly beneath the centered H1
//     inside the hero image area — a smaller, lighter line for hero-level
//     supporting copy (e.g. the Future Residents Club welcome blurb
//     "We don't just rent apartments…"). The subtitle is a per-instance
//     richtext token bound via the shared `tokenPrefix` pattern, so a
//     page's section props can seed a default and each property still
//     overrides copy per-community from the inline editor.
//   • Backwards compatible: when neither a `subtitle` prop nor a stored
//     subtitle token value exists for an instance, nothing renders — every
//     interior page that doesn't opt in stays pixel-identical to the parent.
//
// Everything else — the gallery-keyword-scored background photo, the
// bottom wave band, the schema-lint anchors, and the section sizing — is
// copied verbatim from the parent. When the parent changes, re-diff.
import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useGallery, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
// WaveBand lives in the parent theme under `components/_waveBand.tsx`.
// Deep-importing from the parent's `src/` is a lint error, so we mirror
// the tiny WaveBand piece locally next to this override.
import { WaveBand } from './_waveBand';

// ============================================================================
// Relevant-photo selection
// ============================================================================

const CATEGORY_KEYWORDS: Record<string, string[]> = {
  interior: [
    'interior', 'apartment', 'unit', 'living', 'living room', 'kitchen', 'bedroom',
    'bath', 'bathroom', 'dining', 'home', 'closet', 'hardwood', 'granite', 'stainless',
    'appliance', 'window', 'floor', 'cabinet', 'counter',
  ],
  amenities: [
    'amenity', 'pool', 'fitness', 'gym', 'clubhouse', 'lounge', 'spa', 'sauna',
    'courtyard', 'grill', 'bbq', 'business', 'coffee', 'game', 'theater', 'yoga',
    'rooftop', 'deck', 'fire pit', 'cabana', 'club', 'social',
  ],
  pool: ['pool', 'swim', 'cabana', 'sundeck', 'sun deck', 'hot tub', 'spa', 'lounge'],
  fitness: ['fitness', 'gym', 'yoga', 'exercise', 'workout', 'weight', 'training'],
  community: [
    'exterior', 'building', 'community', 'aerial', 'entrance', 'lobby', 'leasing',
    'courtyard', 'landscap', 'grounds', 'facade', 'property',
  ],
  exterior: ['exterior', 'building', 'facade', 'aerial', 'entrance', 'street', 'architecture', 'front'],
  neighborhood: [
    'neighborhood', 'downtown', 'park', 'shopping', 'dining', 'district', 'skyline',
    'street', 'local', 'attraction', 'city', 'restaurant',
  ],
  pets: ['pet', 'dog', 'paw', 'bark park', 'dog park', 'pet spa', 'puppy', 'park'],
  lobby: ['lobby', 'entrance', 'reception', 'leasing', 'lounge', 'clubhouse', 'welcome'],
};

function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return keywords.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

function pickPhotoIndex(
  gallery: PhotoGallery[],
  category: string | undefined,
  rank: number,
  fallbackIndex: number,
): number {
  const wrappedFallback = gallery.length ? ((fallbackIndex % gallery.length) + gallery.length) % gallery.length : fallbackIndex;
  const keywords = category ? CATEGORY_KEYWORDS[category] : undefined;
  if (!gallery.length || !keywords) return wrappedFallback;

  const seen = new Set<string>();
  const scored: { index: number; score: number }[] = [];
  for (let i = 0; i < gallery.length; i++) {
    const photo = gallery[i];
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    scored.push({ index: i, score: scorePhoto(photo, keywords) });
  }

  const matches = scored.filter((s) => s.score > 0).sort((a, b) => b.score - a.score);
  if (!matches.length) return wrappedFallback;
  return matches[Math.min(rank, matches.length - 1)].index;
}

// ============================================================================
// Slot group definition
// ============================================================================

function makeBgImageDef(index: number): SlotGroupDefinition {
  return {
    label: 'Background Image',
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultField: `photo_gallery[${index}].alt_text`,
        defaultValue: 'Page banner',
      },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Page banner',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Component
// ============================================================================

interface HeroImageBGProps {
  tokenPrefix?: string;
  title?: string;
  /**
   * Optional supporting copy rendered directly below the H1 as a lighter,
   * smaller line overlaid on the hero image. Passed as the RICHTEXT default
   * for the per-instance subtitle token so an editor sees the initial copy
   * and can rewrite it per property. Leave undefined on pages that want just
   * a title (the parent-theme behavior).
   */
  subtitle?: string;
  altText?: string;
  photoCategory?: string;
  photoRank?: number;
  imageIndex?: number;
}

export function HeroImageBG({
  tokenPrefix,
  title = 'Page',
  subtitle,
  altText,
  photoCategory,
  photoRank = 0,
  imageIndex = 0,
}: HeroImageBGProps) {
  const prefix = tokenPrefix || 'heroimagebg';
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const bgPath = `${prefix}.bg_image`;

  const gallery = useGallery();
  const resolvedIndex = useMemo(
    () => pickPhotoIndex(gallery, photoCategory, photoRank, imageIndex),
    [gallery, photoCategory, photoRank, imageIndex],
  );
  const runtimeBgDef = useMemo(() => makeBgImageDef(resolvedIndex), [resolvedIndex]);
  const bg = useSlotGroup(bgPath, runtimeBgDef);

  // Only emit a subtitle DOM node when THIS instance was given one via
  // props — otherwise the schema token still exists (so the editor sees it
  // and can add copy later), but no DOM appears, and every parent-inherited
  // hero looks identical to before this override existed.
  const hasSubtitle = typeof subtitle === 'string' && subtitle.trim().length > 0;

  return (
    <>
      <section className="hero-image-bg relative w-full overflow-hidden">
        <EditableSlotGroup groupPath={bgPath} definition={runtimeBgDef}>
          <div className="absolute inset-0">
            {bg.image ? (
              <VibesitesImage
                url={bg.image}
                alt={bg.alt || altText || `${title} banner`}
                priority
                sizes="100vw"
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        </EditableSlotGroup>

        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/45 pointer-events-none" />

        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center text-white px-6 pointer-events-none"
          style={{
            paddingTop: 'calc(var(--offer-banner-height, 0px) + 5rem)',
            transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <RichTextContent
            path={titlePath}
            defaultValue={`<p>${title}</p>`}
            as="h1"
            className="hero-image-bg__title font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium uppercase tracking-wider leading-tight break-words pointer-events-auto"
          />
          {hasSubtitle && (
            <RichTextContent
              path={subtitlePath}
              defaultValue={subtitle!}
              as="div"
              className="hero-image-bg__subtitle mt-4 max-w-2xl font-body text-sm sm:text-base md:text-base leading-relaxed break-words pointer-events-auto [&_a]:underline [&_p]:mb-2 last:[&_p]:mb-0"
            />
          )}
        </div>
      </section>

      <WaveBand className="bg-[var(--vibe-color-surface)]" height={70} spacing={15} amp={25} />

      {/* Schema-lint anchors — hidden, never interactive. They carry the
          canonical literal paths so the static linter can match every schema
          key; the visible wrappers above use the per-instance dynamic path. */}
      <RichTextContent
        path="heroimagebg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="heroimagebg.subtitle"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="heroimagebg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .hero-image-bg {
          min-height: calc(70vh - 150px);
        }
        @media (min-width: 768px) {
          .hero-image-bg {
            min-height: calc(92vh - var(--offer-banner-height, 0px) - 100px);
          }
        }
        .hero-image-bg__title {
          text-shadow: 0 1px 10px rgba(0, 0, 0, 0.35);
        }
        .hero-image-bg__subtitle {
          text-shadow: 0 1px 8px rgba(0, 0, 0, 0.45);
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroImageBG',
  displayName: 'Hero – Image Background',
  tokens: {
    'heroimagebg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'heroimagebg.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      description:
        'Optional supporting copy rendered directly below the H1 inside the hero image. Leave blank on pages that only need a title.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'heroimagebg.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultField: 'photo_gallery[0].alt_text',
          defaultValue: 'Page banner',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(HeroImageBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroImageBG;
